<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useLifeListStore } from '@/composables/life/life'
import { formatDate, formatFriendlyTime } from '@/utils/time'
import { currentLang } from '@/composables/settings/storage'
import { cutTextAndEscape, removeHTMLTags } from '@/utils/util'
import { auditModeShow } from '@/utils/util'
import defaultAvatar from '@/assets/default_avatar.jpg'

const { t } = useI18n()
const router = useRouter()
const lifeListStore = useLifeListStore()

const props = defineProps({
  lifeInfo: {
    type: Object,
    required: true
  },
  showCreator: {
    type: Boolean,
    default: false,
    required: false
  },
  searchKey: {
    type: String,
    default: '',
    required: false
  },
  isDetail: {
    type: Boolean,
    default: true,
    required: false
  },
  showLifeRang: {
    type: Boolean,
    default: true,
    required: false
  },
  portrait: {
    type: Boolean,
    default: false,
    required: false
  }
})

const lifeInfo = computed(() => {
  console.log('lifeInfo', props.lifeInfo)
  return props.lifeInfo
})
const boxSize = 20

const lifeRang = computed(() => {
  const birthday = lifeInfo.value?.birthday?.date
  const dayDied = lifeInfo.value?.dayDied?.date
  if (!birthday || !dayDied) return ''
  const birthYear = new Date(birthday).getFullYear()
  const deathYear = new Date(dayDied).getFullYear()
  if (!birthYear || !deathYear) return ''
  return birthYear + '-' + deathYear
})

const fullName = computed(() => {
  if (props.isDetail) return lifeInfo.value?.name?.fullName || ''
  return lifeInfo.value?.life?.name?.fullName || ''
})

const avatar = computed(() => {
  if (props.isDetail) return lifeInfo.value?.avatar
  return lifeInfo.value?.life?.avatar
})

const avatarSrc = computed(() => {
  return avatar.value?.middleQuality || avatar.value?.url || defaultAvatar
})

const avatarRatio = computed(() => {
  const w = avatar.value?.w
  const h = avatar.value?.h
  if (!w || !h) return 1
  return w / h
})

const htmlEntityMap: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'"
}

function decodeHtmlEntities(text: string) {
  return text
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec) => String.fromCodePoint(parseInt(dec, 10)))
    .replace(/&([a-z]+);/gi, (full, name) => htmlEntityMap[name.toLowerCase()] ?? full)
}

const firstArticlePreview = computed(() => {
  const rawContent = lifeInfo.value?.firstArticle?.content || ''
  const plainText = removeHTMLTags(rawContent)
  return decodeHtmlEntities(plainText).replace(/\s+/g, ' ').trim()
})

function getImageSizeClass(w: number, h: number) {
  const radio = w / h
  if (w > h) {
    const h = boxSize / radio
    return 'w-' + boxSize + ' h-' + h
  } else if (w < h) {
    const w = boxSize * radio
    return 'w-' + w + ' h-' + boxSize
  } else {
    return 'w-' + (boxSize - 4) + ' h-' + (boxSize - 4)
  }
}
</script>

<template>
  <div
    @click="
      router.push(
        lifeInfo?.life ? `/m/${lifeInfo?.life?.memorialServices[0]?.id}` : `/m/${lifeInfo?.memorialServices[0]?.id}`
      )
    "
    class="cursor-pointer py-3"
    :class="
      props.portrait && props.isDetail
        ? 'bg-card rounded-2 w-auto min-w-40 border-1px'
        : `w-full flex justify-start items-start ${props.isDetail ? 'min-h-28' : 'min-h-20'}`
    ">
    <template v-if="props.portrait && props.isDetail">
      <div class="w-full flex flex-col items-center">
        <div class="w-full flex justify-center items-center min-h-32">
          <MemorizeAvatar :src="avatarSrc" :ratio="avatarRatio" :size="'medium'" />
        </div>
        <div class="mt-1 w-full flex flex-col items-center px-2">
          <div class="w-full text-normal font-bold text-main text-center line-clamp-1">
            <template v-if="props.searchKey && fullName.includes(props.searchKey)">
              <span v-for="(char, index) in fullName" :key="index">
                <span v-if="props.searchKey.includes(char)" class="highlighted-text">{{ char }}</span>
                <span v-else>{{ char }}</span>
              </span>
            </template>
            <template v-else>
              {{ fullName }}
            </template>
          </div>
          <div v-if="!auditModeShow && props.showLifeRang && lifeRang" class="my-1 text-sm text-second">
            {{ lifeRang }}
          </div>
          <div v-if="props.showCreator" class="mt-1 text-xs text-altar text-center">
            {{
              t('open_life.creatorInfo', {
                creator: cutTextAndEscape(lifeInfo.creator.name.fullName, 7),
                createTime: formatDate(lifeInfo.createdAt, currentLang)
              })
            }}
          </div>
        </div>
      </div>
    </template>
    <template v-else>
      <img
        v-if="props.isDetail ? lifeInfo?.avatar?.middleQuality : lifeInfo?.life?.avatar?.middleQuality"
        w-19.5
        h-26
        :src="props.isDetail ? lifeInfo?.avatar?.middleQuality : lifeInfo?.life?.avatar?.middleQuality"
        aspect-fit
        rounded-1.5
        object-contain
        mt-1
        ml-0.5 />
      <div flex flex-col justify-center items-start v-if="props.isDetail" flex-1 ml-4 mt-1>
        <div flex justify-start items-center>
          <div text-lg mr-1>
            <template v-if="props.searchKey && fullName.includes(props.searchKey)">
              <span v-for="(char, index) in fullName" :key="index">
                <span v-if="props.searchKey.includes(char)" class="highlighted-text" text-normal font-bold>{{
                  char
                }}</span>
                <span v-else text-normal font-bold>{{ char }}</span>
              </span>
              <span v-if="!auditModeShow && props.showLifeRang && lifeRang"> ({{ lifeRang }})</span>
            </template>
            <template v-else>
              <span text-normal font-bold mr-1> {{ fullName }}</span>
              <span v-if="!auditModeShow && props.showLifeRang && lifeRang"> ({{ lifeRang }})</span>
            </template>
          </div>
        </div>
        <div v-if="props.showCreator" mt-1>
          <div text-sm text-altar>
            {{
              t('open_life.creatorInfo', {
                creator: cutTextAndEscape(lifeInfo.creator.name.fullName, 7),
                createTime: formatDate(lifeInfo.createdAt, currentLang)
              })
            }}
          </div>
        </div>
        <div
          :class="{ 'text-sm': props.showCreator, 'text-base': !props.showCreator }"
          text-third
          text-sm
          mt-2
          line-clamp-2>
          {{ firstArticlePreview }}
        </div>
      </div>
      <div v-else w-full flex-1 ml-6>
        <div flex flex-col justify-start h-20>
          <div text-lg font-bold mt-1>
            {{ t('open_life.memorial_name', { name: fullName }) }}
          </div>
          <div text-sm text-third w-full mt-2>
            {{
              t('open_life.last_visited_at') +
              (lifeInfo?.visitedAt ? formatFriendlyTime(lifeInfo?.visitedAt, currentLang) : '')
            }}
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.highlighted-text {
  color: #ffd700;
  font-weight: bold;
}
</style>
